/**
 * Fias Leads — CSV column-mapping model (pure functions, no React).
 *
 * The wizard's structural transforms live here: which CSV column feeds
 * which target field, N:1 joins (First + Last → one field), and 1:N
 * splits ("Full Name" → first/last; "Last, First" → parts swapped).
 * The output of applyMapping() is the target-shaped row envelope the
 * server preview/execute endpoints receive — SEMANTIC normalization
 * (email lowercasing, value coercion, stage matching, dedup) is the
 * server's job so the preview always shows exactly what gets written.
 */

export type ImportEntityType = 'contact' | 'company' | 'deal';

/** How one target field gets its value from a CSV row. */
export type ColumnSource =
  | { kind: 'none' }
  | { kind: 'column'; header: string }
  | { kind: 'join'; headers: string[]; separator: string }
  | {
      kind: 'split';
      header: string;
      delimiter: string;
      /** 0-based part index after splitting on the delimiter. */
      partIndex: number;
      /** When true, joins parts [partIndex..] back with the delimiter
       * ("everything after the 1st part"). */
      takeRemainder: boolean;
    };

export type ImportMapping = Record<string, ColumnSource>;

export interface ImportFieldDescriptor {
  field: string;
  label: string;
  required?: boolean;
  /** Regex synonyms tested against normalizeHeader() output. */
  synonyms: ReadonlyArray<RegExp>;
  /** Short helper text shown under the field in the mapping UI. */
  hint?: string;
}

export const IMPORT_CHUNK_SIZE = 200;
export const MAX_PREVIEW_ROWS_PER_REQUEST = 1000;
export const MAX_IMPORT_ROWS = 5000;
export const MAX_IMPORT_FILE_BYTES = 10 * 1024 * 1024;

/** "E-mail Address" → "emailaddress"; "First_Name" → "firstname";
 * "Name > First" → "namefirst". All non-alphanumerics are stripped so
 * exotic separators (>, /, :) don't defeat the synonym regexes. */
export function normalizeHeader(header: string): string {
  return header
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

/**
 * Auto-maps CSV headers to target fields. Fields are matched in list
 * order (more specific descriptors first) and each header is consumed
 * at most once, so "Name" and "Company Name" land on different fields.
 * Unmatched fields get {kind:'none'} (explicit skip).
 */
export function autoMapColumns(
  headers: readonly string[],
  fields: ReadonlyArray<ImportFieldDescriptor>,
): ImportMapping {
  const mapping: ImportMapping = {};
  const consumed = new Set<string>();
  for (const f of fields) {
    const matched = headers.find((h) => {
      if (consumed.has(h)) return false;
      const normalized = normalizeHeader(h);
      if (f.synonyms.some((re) => re.test(normalized))) return true;
      return normalized === normalizeHeader(f.field) || normalized === normalizeHeader(f.label);
    });
    if (matched) {
      consumed.add(matched);
      mapping[f.field] = { kind: 'column', header: matched };
    } else {
      mapping[f.field] = { kind: 'none' };
    }
  }
  return mapping;
}

/** Splits `value` on `delimiter` and picks a part (or the remainder).
 * Out-of-range part → ''. Parts are trimmed. */
export function splitPart(
  value: string,
  delimiter: string,
  partIndex: number,
  takeRemainder: boolean,
): string {
  if (!delimiter) return value.trim();
  const parts = value.split(delimiter);
  if (partIndex >= parts.length) return '';
  if (takeRemainder) return parts.slice(partIndex).join(delimiter).trim();
  return (parts[partIndex] ?? '').trim();
}

/** Resolves one target-field value from a raw CSV row. */
export function resolveCellValue(row: Record<string, string>, source: ColumnSource): string {
  switch (source.kind) {
    case 'none':
      return '';
    case 'column':
      return (row[source.header] ?? '').trim();
    case 'join':
      return source.headers
        .map((h) => (row[h] ?? '').trim())
        .filter((part) => part !== '')
        .join(source.separator);
    case 'split':
      return splitPart(
        row[source.header] ?? '',
        source.delimiter,
        source.partIndex,
        source.takeRemainder,
      );
  }
}

/**
 * Applies the mapping to every row → target-shaped rows. Fields mapped
 * to {kind:'none'} and empty resolved values are omitted entirely so
 * the server's strict row schemas see only real data.
 */
export function applyMapping(
  rows: ReadonlyArray<Record<string, string>>,
  mapping: ImportMapping,
): Array<Record<string, string>> {
  return rows.map((row) => {
    const out: Record<string, string> = {};
    for (const [field, source] of Object.entries(mapping)) {
      if (source.kind === 'none') continue;
      const value = resolveCellValue(row, source);
      if (value !== '') out[field] = value;
    }
    return out;
  });
}

/** Headers referenced by no source — the "will not be imported" list. */
export function listUnmappedHeaders(headers: readonly string[], mapping: ImportMapping): string[] {
  const referenced = new Set<string>();
  for (const source of Object.values(mapping)) {
    if (source.kind === 'column') referenced.add(source.header);
    else if (source.kind === 'join') source.headers.forEach((h) => referenced.add(h));
    else if (source.kind === 'split') referenced.add(source.header);
  }
  return headers.filter((h) => !referenced.has(h));
}

/** Required fields whose source is 'none' — gates Map → Preview. */
export function listMissingRequired(
  fields: ReadonlyArray<ImportFieldDescriptor>,
  mapping: ImportMapping,
): ImportFieldDescriptor[] {
  return fields.filter((f) => f.required && (mapping[f.field] ?? { kind: 'none' }).kind === 'none');
}

/** "jeff.smith@x.com" → "Jeff Smith" — mirrors the server's email name
 * fallback, used to pre-fill the row-editor's suggestion. */
export function nameFromEmail(email: string): string {
  return email
    .split('@')[0]
    .split(/[._-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function chunkArray<T>(items: ReadonlyArray<T>, size: number): T[][] {
  const chunks: T[][] = [];
  for (let offset = 0; offset < items.length; offset += size) {
    chunks.push(items.slice(offset, offset + size));
  }
  return chunks;
}
